import { getCountryConfig } from "./countryConfig";

// Initial fallback centre = active country's map centre (Mali by default).
// Stays in sync with /app/backend/countries/<cc>.json — same source of truth
// as the admin Command Center, so multi-country deployments don't need a
// code change to recentre.
const _ccMap = (getCountryConfig() || {}).map || {};
export const FALLBACK = {
  lat: typeof _ccMap.center_lat === "number" ? _ccMap.center_lat : 12.6392,
  lng: typeof _ccMap.center_lng === "number" ? _ccMap.center_lng : -8.0029,
};

// Iter77 — Citizen /carte specific fallback. The country-wide FALLBACK
// above is deliberately wide (e.g. 17.5°N, -2.5°E for Mali) so the admin
// national map can show every alert across the country. That view lands
// on empty Sahara at zoom 11, so citizens who deny GPS were seeing dunes
// instead of their capital. CITIZEN_FALLBACK pins the citizen map to the
// country's largest city / capital (from `map.citizen_center_*` in the
// country config, falling back to Bamako for Mali).
export const CITIZEN_FALLBACK = {
  lat:
    typeof _ccMap.citizen_center_lat === "number"
      ? _ccMap.citizen_center_lat
      : 12.6392, // Bamako
  lng:
    typeof _ccMap.citizen_center_lng === "number"
      ? _ccMap.citizen_center_lng
      : -8.0029, // Bamako
};

export const USER_SOURCE     = "user-pos";
export const SVC_SOURCE      = "emergency-services";
export const HEAT_SOURCE     = "safety-heatmap";
export const PIN_SOURCE      = "report-pin";
export const TOMTOM_INC_SRC  = "tomtom-incidents";
export const CROWD_ACC_SRC   = "crowd-accidents";
export const TRAFFIC_RASTER  = "tomtom-flow";

// TomTom iconCategory → emoji + color (compact mapping)
// Reference: 0=unknown, 1=accident, 2=fog, 3=danger, 4=rain, 5=ice, 6=jam,
// 7=lane closed, 8=road closed, 9=road works, 10=wind, 11=flooding, 14=broken vehicle.
const TOMTOM_ICON = {
  1:  { label: "Accident",          color: "#DC2626" },
  3:  { label: "Danger",            color: "#F97316" },
  6:  { label: "Embouteillage",     color: "#EAB308" },
  7:  { label: "Voie fermée",       color: "#F59E0B" },
  8:  { label: "Route fermée",      color: "#7C2D12" },
  9:  { label: "Travaux",           color: "#F59E0B" },
  11: { label: "Inondation",        color: "#0EA5E9" },
  14: { label: "Véhicule en panne", color: "#9333EA" },
};
export const tomtomMeta = (cat) => TOMTOM_ICON[cat] || { label: "Incident", color: "#64748B" };

// HIGH / MEDIUM / LOW accuracy buckets drive the ring color so users can read
// trust at a glance.
export const accColor = (acc) => {
  if (acc == null) return "#64748B";
  if (acc <= 20) return "#10B981";   // emerald — HIGH
  if (acc <= 100) return "#F59E0B";  // amber  — MEDIUM
  return "#EF4444";                   // red    — LOW
};

export const hasWebGL = () => {
  try {
    const c = document.createElement("canvas");
    return !!(
      window.WebGLRenderingContext
      && (c.getContext("webgl") || c.getContext("experimental-webgl"))
    );
  } catch (_e) { return false; }
};

// Iter83 — Real glyph icons per POI category (hospital cross, police
// shield, fire flame, FAMA star, embassy flag) instead of a plain
// colored dot, so citizens recognize the service type at a glance
// without needing to read the label text first.
const _drawCross = (ctx, cx, cy, r) => {
  const armW = r * 0.5, armL = r * 1.15;
  ctx.fillRect(cx - armW / 2, cy - armL / 2, armW, armL);
  ctx.fillRect(cx - armL / 2, cy - armW / 2, armL, armW);
};

const _drawShield = (ctx, cx, cy, r) => {
  const w = r * 1.05, h = r * 1.3;
  ctx.beginPath();
  ctx.moveTo(cx, cy - h / 2);
  ctx.lineTo(cx + w / 2, cy - h / 2 + h * 0.14);
  ctx.lineTo(cx + w / 2, cy + h * 0.05);
  ctx.quadraticCurveTo(cx + w / 2, cy + h * 0.36, cx, cy + h / 2);
  ctx.quadraticCurveTo(cx - w / 2, cy + h * 0.36, cx - w / 2, cy + h * 0.05);
  ctx.lineTo(cx - w / 2, cy - h / 2 + h * 0.14);
  ctx.closePath();
  ctx.fill();
};

const _drawFlame = (ctx, cx, cy, r) => {
  const fw = r * 0.95, fh = r * 1.3;
  ctx.beginPath();
  ctx.moveTo(cx, cy + fh / 2);
  ctx.bezierCurveTo(cx - fw * 0.62, cy + fh * 0.08, cx - fw * 0.5, cy - fh * 0.38, cx, cy - fh / 2);
  ctx.bezierCurveTo(cx + fw * 0.5, cy - fh * 0.38, cx + fw * 0.62, cy + fh * 0.08, cx, cy + fh / 2);
  ctx.closePath();
  ctx.fill();
};

const _drawStar = (ctx, cx, cy, r) => {
  const outerR = r * 0.85, innerR = outerR * 0.42, spikes = 5;
  let rot = (Math.PI / 2) * 3;
  const step = Math.PI / spikes;
  ctx.beginPath();
  ctx.moveTo(cx, cy - outerR);
  for (let i = 0; i < spikes; i++) {
    ctx.lineTo(cx + Math.cos(rot) * outerR, cy + Math.sin(rot) * outerR);
    rot += step;
    ctx.lineTo(cx + Math.cos(rot) * innerR, cy + Math.sin(rot) * innerR);
    rot += step;
  }
  ctx.closePath();
  ctx.fill();
};

const _drawFlag = (ctx, cx, cy, r) => {
  ctx.beginPath();
  ctx.rect(cx - r * 0.06, cy - r * 0.9, r * 0.12, r * 1.7);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx + r * 0.06, cy - r * 0.85);
  ctx.lineTo(cx + r * 0.78, cy - r * 0.55);
  ctx.lineTo(cx + r * 0.06, cy - r * 0.25);
  ctx.closePath();
  ctx.fill();
};

const SERVICE_GLYPHS = {
  hospital: _drawCross,
  police: _drawShield,
  fire: _drawFlame,
  fama: _drawStar,
  embassy: _drawFlag,
};

// Builds a static (non-animated) badge icon for MapLibre `addImage`:
// colored circle + white border + white category glyph on top. `size`
// is the PHYSICAL canvas size — pair with `pixelRatio: 2` at addImage
// time so it renders at half that as the logical/on-map size.
export const buildServiceIcon = (category, color, size = 64) => {
  const c = document.createElement("canvas");
  c.width = size;
  c.height = size;
  const ctx = c.getContext("2d");
  const cx = size / 2, cy = size / 2, r = size * 0.42;

  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.lineWidth = size * 0.06;
  ctx.strokeStyle = "#ffffff";
  ctx.stroke();

  ctx.fillStyle = "#ffffff";
  const glyph = SERVICE_GLYPHS[category];
  if (glyph) {
    glyph(ctx, cx, cy, r);
  } else {
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.28, 0, Math.PI * 2);
    ctx.fill();
  }

  return ctx.getImageData(0, 0, size, size);
};

// Mali flag colors used in the pulsing GPS marker halo.
const MALI_GREEN = "#14B53A";
const MALI_YELLOW = "#FCD116";
const MALI_RED = "#CE1126";

// Pulsing dot helper: builds a tiny canvas image we register with MapLibre
// then reference in a `symbol` layer for a smooth animated GPS marker.
// Layered tricolor halo (Mali flag green/yellow/red) underneath a solid
// accuracy-color core. Subtle but national.
export const buildPulsingDot = (map, size, color) => ({
  width: size,
  height: size,
  data: new Uint8Array(size * size * 4),
  context: null,
  onAdd() {
    const c = document.createElement("canvas");
    c.width = this.width;
    c.height = this.height;
    this.context = c.getContext("2d");
  },
  render() {
    const duration = 1800;
    const t = (performance.now() % duration) / duration;
    const cx = this.width / 2;
    const cy = this.height / 2;
    const innerR = (size / 2) * 0.28;
    const outerR = (size / 2) * 0.95 * t + innerR;
    const ctx = this.context;
    ctx.clearRect(0, 0, this.width, this.height);

    // Tricolor expanding ring (green → yellow → red over its life)
    const ringHex = t < 0.33 ? MALI_GREEN : t < 0.66 ? MALI_YELLOW : MALI_RED;
    const alpha = Math.floor((1 - t) * 200).toString(16).padStart(2, "0");
    ctx.beginPath();
    ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
    ctx.fillStyle = `${ringHex}${alpha}`;
    ctx.fill();

    // Soft inner glow (color = accuracy bucket)
    const glowGrad = ctx.createRadialGradient(cx, cy, innerR * 0.5, cx, cy, innerR * 2.2);
    glowGrad.addColorStop(0, `${color}cc`);
    glowGrad.addColorStop(1, `${color}00`);
    ctx.beginPath();
    ctx.arc(cx, cy, innerR * 2.2, 0, Math.PI * 2);
    ctx.fillStyle = glowGrad;
    ctx.fill();

    // Solid core dot with white casing for crispness
    ctx.beginPath();
    ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.strokeStyle = "#ffffff";
    ctx.lineWidth = 2.5 + 1.5 * (1 - t);
    ctx.fill();
    ctx.stroke();

    this.data = ctx.getImageData(0, 0, this.width, this.height).data;
    map.triggerRepaint();
    return true;
  },
});
